/* RESPONSIBLE TEAM: team-automation-foundations */
/* eslint-disable @intercom/intercom/no-bare-strings */
import type IntlService from 'ember-intl/services/intl';
import {
  and,
  wrap,
} from 'embercom/components/reporting/bespoke/sdr-performance/qualification-funnel/series-config';

type CategoryFilter = {
  type: 'category';
  data: {
    property: string;
    attribute: string;
    values: unknown[];
  };
};

type AndFilter = {
  type: 'and';
  filters: Filter[];
};

type Filter = CategoryFilter | AndFilter;

type Series = {
  label?: string;
  metric_id: string;
  filters: Filter | undefined;
};

type SeriesFilter = {
  name: string;
  series: Series;
};

type ChartDataPoint = { data: ReadonlyArray<number> };
type ChartDataResponse = ReadonlyArray<ChartDataPoint>;

const filters = {
  get sdrParticipated(): CategoryFilter {
    return {
      type: 'category',
      data: {
        property: 'fin_sdr.participated',
        attribute: 'conversation.fin_sdr.participated',
        values: [true],
      },
    };
  },

  routingOutcome(outcomeTitle: string): CategoryFilter {
    return {
      type: 'category',
      data: {
        property: 'fin_sdr.routing_outcome.title',
        attribute: 'conversation.fin_sdr.routing_outcome_title',
        values: [outcomeTitle],
      },
    };
  },

  get meetingBooked(): CategoryFilter {
    return {
      type: 'category',
      data: {
        property: 'fin_sdr.meeting_booked',
        attribute: 'conversation.fin_sdr.meeting_booked',
        values: [true],
      },
    };
  },

  get meetingBookedViaButton(): CategoryFilter {
    return {
      type: 'category',
      data: {
        property: 'fin_sdr.meeting_booked_via_button',
        attribute: 'conversation.fin_sdr.meeting_booked_via_button',
        values: [true],
      },
    };
  },
};

function funnelSeries(filter: Filter, label?: string): Series {
  return {
    label,
    metric_id: 'v1.new_conversations',
    filters: wrap(filter) as Filter | undefined,
  };
}

function config(name: string, filter: Filter): SeriesFilter {
  return { name, series: funnelSeries(filter, name) };
}

type Outcome = { identifier: string; title: string };

export const SUFFIXES = {
  total: '_total',
  booked: '_booked',
  buttonBooked: '_buttonBooked',
} as const;

export const SdrMeetingBookingFunnelConfig = {
  buildChartSeriesFilters(routingOutcomes: Outcome[]): SeriesFilter[] {
    let seriesFilters: SeriesFilter[] = [];

    routingOutcomes.forEach((outcome) => {
      let baseFilter = and(
        filters.sdrParticipated,
        filters.routingOutcome(outcome.title),
      ) as Filter;

      seriesFilters.push(
        config(`${outcome.identifier}${SUFFIXES.total}`, baseFilter),
        config(
          `${outcome.identifier}${SUFFIXES.booked}`,
          and(baseFilter, filters.meetingBooked) as Filter,
        ),
        config(
          `${outcome.identifier}${SUFFIXES.buttonBooked}`,
          and(baseFilter, filters.meetingBookedViaButton) as Filter,
        ),
      );
    });

    return seriesFilters;
  },

  getChartSeriesWithLabels(_intl: IntlService, routingOutcomes: Outcome[]) {
    let seriesFilters = this.buildChartSeriesFilters(routingOutcomes);
    return seriesFilters.map((c) => ({ ...c.series, label: c.name }));
  },

  getMetricsFromChartDataRequest(
    chartData: ChartDataResponse,
    routingOutcomes: Outcome[],
  ): Record<string, number> {
    let seriesFilters = this.buildChartSeriesFilters(routingOutcomes);
    let result: Record<string, number> = {};
    for (let i = 0; i < chartData.length; i++) {
      result[seriesFilters[i].name] = chartData[i].data[0] || 0;
    }
    return result;
  },
};

// Layout coordination between the parent's grid_height (card height in row units) and the
// child's chart.height (Highcharts container in px). Centralised here so callers don't drift
// out of sync — both scale with outcome count.
//
// - gridRows: row-span on the static-report grid (each row is 80px). 4 rows covers up to 2
//   outcomes; each additional outcome adds 1 row, capped at 10 (the max row-span class
//   defined in static-report-chart-layout.hbs).
// - chartPx: Highcharts chart.height. Minimum 160px keeps the legend + bars readable for
//   1–2 outcomes; each bar gets ~60px of vertical room.
export function getCardLayout(outcomeCount: number): { gridRows: number; chartPx: number } {
  let gridRows = Math.min(10, 4 + Math.max(0, outcomeCount - 2));
  let chartPx = Math.max(160, outcomeCount * 60 + 40);
  return { gridRows, chartPx };
}
